/* T3D · Bundles — blocs « Paliers de quantité » (<t3d-paliers>) et « Complète ton achat » (<t3d-bundle>) pour Dawn 16.1.
   Calculs : assets/t3d-bundles-core.js · Spec : docs/superpowers/specs/2026-10-14-bundles-paliers-design.md */
(() => {
  'use strict';
  if (customElements.get('t3d-paliers')) return;
  const C = window.T3DBundlesCore;
  if (!C) return;

  const { NBSP } = C;
  const CHECK =
    '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7"/></svg>';
  const boxes = {};

  const esc = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const readConfig = (el) => {
    try {
      return JSON.parse(el.querySelector('script[data-t3d-config]').textContent);
    } catch (error) {
      return null;
    }
  };

  // Produit sérialisé par snippets/t3d-product-json.liquid
  const indexProduct = (product) => {
    if (!product || !Array.isArray(product.variants)) return null;
    const swatches = new Map((product.swatches || []).map(([index, name, color]) => [`${index}|${name}`, color]));
    return {
      ...product,
      byId: new Map(product.variants.map((variant) => [variant.id, variant])),
      available: product.variants.filter((variant) => variant.available),
      single: product.variants.length === 1,
      colorOf: (variant) =>
        variant ? variant.options.map((option, index) => swatches.get(`${index}|${option}`)).find(Boolean) || null : null,
    };
  };

  const dot = (color) => (color ? `<span class="t3d-dot" style="background:${esc(color)}"></span>` : '');

  const variantSelect = (product, selectedId, attr, label) =>
    `<span class="t3d-vsel">${dot(product.colorOf(product.byId.get(selectedId)))}<select class="t3d-select" ${attr} aria-label="${esc(label)}">${product.variants
      .map(
        (variant) =>
          `<option value="${variant.id}"${variant.id === selectedId ? ' selected' : ''}${variant.available ? '' : ' disabled'}>${esc(variant.title)}</option>`
      )
      .join('')}</select></span>`;

  const stepper = (value, attr, label) =>
    `<span class="t3d-stepper" ${attr}><button type="button" class="t3d-stepper__btn" data-step="-1" aria-label="Retirer · ${esc(label)}">−</button><span class="t3d-stepper__value${value ? '' : ' is-zero'}">${value}</span><button type="button" class="t3d-stepper__btn" data-step="1" aria-label="Ajouter · ${esc(label)}">+</button></span>`;

  const priceHtml = (tot, fmt, stack, id) =>
    `<span class="t3d-price${stack ? ' t3d-price--stack' : ''}"${id ? ` id="${id}"` : ''}><span class="t3d-price__now">${C.formatMoney(tot.finalCents, fmt)}</span>${
      tot.discountCents ? `<s class="t3d-price__was">${C.formatMoney(tot.fullCents, fmt)}</s>` : ''
    }</span>`;

  const refocus = (root, selector) => root.querySelector(selector)?.focus();

  const productPath = (handle) => `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}`;

  // ——— Zone d'achat : un coordinateur par section (paliers + bundle + bouton natif) ———
  class BuyBox {
    static get(sectionId) {
      const current = boxes[sectionId];
      // Dawn (HTMLUpdateUtility.viewTransition) insère la nouvelle zone produit avant l'ancienne, retirée 500 ms plus tard :
      // la zone à jour est celle que renvoie getElementById, même si l'ancienne est encore connectée.
      if (current?.info && current.info === document.getElementById(`MainProduct-${sectionId}`)) return current;
      current?.unsubscribe?.();
      return (boxes[sectionId] = new BuyBox(sectionId));
    }

    constructor(sectionId) {
      this.sectionId = sectionId;
      this.info = document.getElementById(`MainProduct-${sectionId}`);
      this.formEl = this.info?.querySelector('product-form') || null;
      this.paliers = null;
      this.bundle = null;
      if (!this.formEl) return;
      this.button = this.formEl.querySelector('[type="submit"]');
      // Phase de capture : passe avant l'écouteur de product-form.js posé sur le <form>.
      this.formEl.addEventListener('submit', (event) => this.onSubmit(event), true);
      // Sans paliers, la quantité native (boutons − / + de Dawn ou saisie) fixe le nombre d'unités du produit principal.
      const onQuantity = (event) => event.target.matches?.('.quantity__input') && this.refresh();
      this.info.addEventListener('change', onQuantity);
      this.info.addEventListener('input', onQuantity);
      if (typeof subscribe === 'function') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.sectionId !== this.sectionId) return;
          this.paliers?.onVariantChange(event.data.variant);
          this.bundle?.onVariantChange(event.data.variant);
          this.refresh();
        });
      }
    }

    variantId() {
      const value = Number(this.formEl.querySelector('input[name="id"]')?.value);
      return value || null;
    }

    nativeQty() {
      const value = parseInt(this.info.querySelector('.quantity__input')?.value, 10);
      return value > 0 ? value : 1;
    }

    mainUnits() {
      if (this.paliers) return this.paliers.units();
      const id = this.variantId();
      return id ? Array(this.nativeQty()).fill(id) : [];
    }

    items() {
      return C.buildCartItems(this.mainUnits(), this.bundle ? this.bundle.selections() : []);
    }

    // Vrai quand l'ajout diffère de ce que product-form.js enverrait seul (produit principal × quantité native).
    custom() {
      if (this.paliers) return this.paliers.missing() > 0 || C.articleCount(this.items()) > 1;
      return Boolean(this.bundle && this.bundle.selections().length);
    }

    // Total de l'ajout (palier choisi + accessoires cochés), ou null sans bloc T3D actif.
    totals() {
      const main = this.paliers ? this.paliers.selectedTotals() : this.bundle ? this.bundle.mainTotals() : null;
      if (!main) return null;
      const chosen = this.bundle ? this.bundle.accessories.filter((a) => a.checked) : [];
      return chosen
        .map((a) => this.bundle.accessoryTotals(a))
        .reduce(
          (acc, t) => ({ fullCents: acc.fullCents + t.fullCents, discountCents: acc.discountCents + t.discountCents, finalCents: acc.finalCents + t.finalCents }),
          main
        );
    }

    // Diffuse l'état de l'ajout à la barre d'achat mobile (assets/t3d-motion.js) : prix total et articles manquants.
    // Gardé aussi dans window.T3D_BUYBOX : la barre peut naître après le premier calcul.
    announce() {
      const tot = this.totals();
      const fmt = (this.paliers || this.bundle)?.fmt;
      const detail = {
        sectionId: this.sectionId,
        price: tot ? C.formatMoney(tot.finalCents, fmt) : '',
        was: tot && tot.discountCents ? C.formatMoney(tot.fullCents, fmt) : '',
        missing: this.paliers ? this.paliers.missing() : 0,
      };
      window.T3D_BUYBOX = detail;
      document.dispatchEvent(new CustomEvent('t3d:buybox', { detail }));
    }

    refresh() {
      if (!this.formEl) return;
      const count = C.articleCount(this.items());
      this.info.classList.toggle('t3d-has-paliers', Boolean(this.paliers));
      this.info.classList.toggle('t3d-multi', this.custom());
      this.bundle?.renderMain();
      const label = this.button?.querySelector('span');
      if (label && !this.button.hasAttribute('disabled')) {
        const base = window.variantStrings.addToCart;
        if (this.paliers && this.paliers.missing() > 0) label.textContent = this.paliers.missingLabel();
        else label.textContent = count > 1 ? `${base}${NBSP}· ${count}${NBSP}articles` : base;
      }
      this.announce();
    }

    onSubmit(event) {
      if (!this.custom()) return; // cas simple : product-form.js gère l'ajout
      event.preventDefault();
      event.stopPropagation();
      if (this.paliers && this.paliers.missing() > 0) return this.paliers.flagMissing();
      if (this.button.getAttribute('aria-disabled') === 'true') return;
      this.add(this.items());
    }

    add(items) {
      const form = this.formEl;
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const spinner = form.querySelector('.loading__spinner');
      form.handleErrorMessage?.();
      this.button.setAttribute('aria-disabled', 'true');
      this.button.classList.add('loading');
      spinner?.classList.remove('hidden');

      const body = { items };
      if (cart) {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        cart.setActiveElement(document.activeElement);
      }

      fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            form.handleErrorMessage?.(response.description || response.message);
            if (typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartError, { source: 't3d-bundles', errors: response.errors || response.description, message: response.message });
            }
            return;
          }
          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }
          if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 't3d-bundles', cartData: response });
          cart.renderContents(response);
        })
        .catch((error) => {
          console.error(error);
          form.handleErrorMessage?.(window.cartStrings.error);
        })
        .finally(() => {
          this.button.classList.remove('loading');
          this.button.removeAttribute('aria-disabled');
          spinner?.classList.add('hidden');
          if (cart?.classList.contains('is-empty')) cart.classList.remove('is-empty');
        });
    }
  }

  // ——— Bloc « Paliers de quantité » ———
  class T3DPaliers extends HTMLElement {
    connectedCallback() {
      if (this.ready || this.closest('quick-add-modal')) return;
      const config = readConfig(this);
      const product = indexProduct(config?.product);
      if (!product || !product.available.length) return this.remove();
      const minUnit = Math.min(...product.available.map((variant) => variant.price));
      const tiers = C.parseTierLines(config.lines, minUnit);
      const box = BuyBox.get(this.dataset.section);
      if (!tiers.length || !box.formEl) return this.remove();

      this.product = product;
      this.fmt = product.moneyFormat;
      this.tiers = [{ qty: 1, discount: null, badge: '' }, ...tiers];
      this.selected = 0;
      this.box = box;
      this.main = box.variantId();
      this.resetUnits();
      this.cards = this.querySelector('.t3d-pal__cards');
      this.cards.addEventListener('click', (event) => this.onClick(event));
      this.cards.addEventListener('change', (event) => this.onChange(event));
      this.cards.addEventListener('keydown', (event) => this.onKeydown(event));
      box.paliers = this;
      this.ready = true;
      this.classList.add('is-ready');
      this.render();
      box.refresh();
    }

    disconnectedCallback() {
      if (this.box?.paliers === this) this.box.paliers = null;
    }

    mainId() {
      const variant = this.product.byId.get(this.main);
      return variant?.available ? variant.id : this.product.available[0].id;
    }

    resetUnits() {
      this.current = Array(this.tiers[this.selected].qty).fill(this.mainId());
      this.message = '';
    }

    units() {
      return this.current.slice();
    }

    selectedQty() {
      return this.tiers[this.selected].qty;
    }

    missing() {
      return this.selectedQty() - this.current.length;
    }

    missingLabel() {
      const n = this.missing();
      return `Répartis encore ${n} article${n > 1 ? 's' : ''}`;
    }

    flagMissing() {
      this.message = `${this.missingLabel()} pour ajouter au panier.`;
      this.render();
    }

    onVariantChange(variant) {
      if (!variant) return;
      this.main = variant.id;
      this.resetUnits();
      this.render();
    }

    lines(ids) {
      return C.groupUnits(ids).map(({ id, quantity }) => ({ quantity, unitCents: this.product.byId.get(id).price }));
    }

    tierTotals(index) {
      const tier = this.tiers[index];
      const ids = index === this.selected ? this.current.slice() : [];
      while (ids.length < tier.qty) ids.push(this.mainId());
      return C.totals(this.lines(ids), tier.discount);
    }

    selectedTotals() {
      return this.tierTotals(this.selected);
    }

    render() {
      const fmt = this.fmt;
      this.cards.innerHTML = this.tiers
        .map((tier, index) => {
          const on = index === this.selected;
          const tot = this.tierTotals(index);
          // Le rôle radio porte sur l'en-tête seul : les menus des unités restent hors de l'élément à rôle (pas de contrôles imbriqués).
          return `<div class="t3d-card${on ? ' is-on' : ''}">
            <div class="t3d-card__head" role="radio" aria-checked="${on}" tabindex="${on ? 0 : -1}" data-tier="${index}">
              <span class="t3d-check t3d-check--round">${CHECK}</span>
              <div class="t3d-card__body">
                <div class="t3d-card__row">
                  <span class="t3d-card__qty">${tier.qty}${NBSP}${tier.qty > 1 ? 'unités' : 'unité'}</span>
                  ${tier.discount ? `<span class="t3d-badge">−${C.discountLabel(tier.discount, fmt)}</span>` : ''}
                  ${tier.badge ? `<span class="t3d-badge t3d-badge--pop">${esc(tier.badge)}</span>` : ''}
                  ${priceHtml(tot, fmt)}
                </div>
                ${tot.discountCents ? `<p class="t3d-card__save">Vous économisez ${C.formatMoney(tot.discountCents, fmt)}</p>` : ''}
              </div>
            </div>
            ${on && tier.qty > 1 && !this.product.single ? `<div class="t3d-card__panel">${this.renderUnits(tier)}</div>` : ''}
          </div>`;
        })
        .join('');
    }

    renderUnits(tier) {
      const product = this.product;
      if (!C.usesRepartition(tier.qty)) {
        return `<div class="t3d-units">${this.current
          .map(
            (id, k) =>
              `<div class="t3d-unit"><span class="t3d-unit__label">Unité${NBSP}${k + 1}</span>${variantSelect(product, id, `data-unit="${k}"`, `Unité ${k + 1}`)}</div>`
          )
          .join('')}</div>`;
      }
      const placed = this.current.length;
      const done = placed === tier.qty;
      const hint = this.message || (done ? '' : `${this.missingLabel()}.`);
      return `<div class="t3d-rep">
        <div class="t3d-rep__head"><span>Répartis tes articles</span><span class="t3d-rep__count${done ? ' is-ok' : ''}">${placed} / ${tier.qty}${done ? ' répartis' : ''}</span></div>
        <div class="t3d-rep__grid">${product.available
          .map(
            (variant) =>
              `<div class="t3d-rep__item">${dot(product.colorOf(variant))}<span class="t3d-rep__name">${esc(variant.title)}</span>${stepper(
                C.countOf(this.current, variant.id),
                `data-rep="${variant.id}"`,
                variant.title
              )}</div>`
          )
          .join('')}</div>
        <p class="t3d-pal__hint" role="status"${hint ? '' : ' hidden'}>${esc(hint)}</p>
      </div>`;
    }

    onClick(event) {
      const step = event.target.closest('[data-step]');
      if (step) return this.onStep(step);
      if (event.target.closest('select, .t3d-card__panel')) return;
      const head = event.target.closest('.t3d-card')?.querySelector('[data-tier]');
      if (head) this.select(Number(head.dataset.tier));
    }

    onStep(button) {
      const id = Number(button.closest('[data-rep]').dataset.rep);
      const qty = this.selectedQty();
      const up = button.dataset.step === '1';
      if (up && this.current.length >= qty) {
        this.message = 'Tous les articles sont répartis : retire d’abord un article d’une autre variante.';
      } else {
        this.current = up ? C.addUnit(this.current, id, qty) : C.removeUnit(this.current, id);
        this.message = '';
      }
      this.render();
      this.box.refresh();
      refocus(this.cards, `[data-rep="${id}"] [data-step="${button.dataset.step}"]`);
    }

    onChange(event) {
      const select = event.target.closest('select[data-unit]');
      if (!select) return;
      this.current[Number(select.dataset.unit)] = Number(select.value);
      this.render();
      this.box.refresh();
      refocus(this.cards, `select[data-unit="${select.dataset.unit}"]`);
    }

    onKeydown(event) {
      const card = event.target.closest('[data-tier]');
      if (!card || event.target !== card) return;
      const index = Number(card.dataset.tier);
      const moves = { ' ': index, Enter: index, ArrowDown: index + 1, ArrowRight: index + 1, ArrowUp: index - 1, ArrowLeft: index - 1 };
      if (!(event.key in moves)) return;
      event.preventDefault();
      this.select(Math.min(Math.max(moves[event.key], 0), this.tiers.length - 1));
    }

    select(index) {
      if (index !== this.selected) {
        this.selected = index;
        this.resetUnits();
        this.render();
        this.box.refresh();
      }
      refocus(this.cards, `[data-tier="${index}"]`);
    }
  }

  customElements.define('t3d-paliers', T3DPaliers);

  const USERS =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/><path d="M21 21v-2a4 4 0 0 0-3-3.85"/></svg>';
  const NEW_TAB =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 17L17 7"/><path d="M8 7h9v9"/></svg>';
  const MAX_ACCESSORY_QTY = 10;

  // ——— Bloc « Complète ton achat » : vente additionnelle, accessoires au prix normal ———
  class T3DBundle extends HTMLElement {
    connectedCallback() {
      if (this.ready || this.closest('quick-add-modal')) return;
      const config = readConfig(this);
      const product = indexProduct(config?.product);
      const box = BuyBox.get(this.dataset.section);
      if (!product || !box.formEl) return this.remove();
      this.accessories = (config.products || [])
        .map(indexProduct)
        .filter((p) => p && p.id !== product.id && p.available.length)
        .map((p) => ({ product: p, variantId: p.available[0].id, quantity: 1, checked: false }));
      if (!this.accessories.length) return this.remove();

      this.product = product;
      this.fmt = product.moneyFormat;
      this.badgeText = config.title || 'Souvent achetés ensemble'; // pas this.title : poserait une infobulle sur tout le bloc
      this.box = box;
      this.inner = this.querySelector('.t3d-bdl__inner');
      this.inner.addEventListener('click', (event) => this.onClick(event));
      this.inner.addEventListener('change', (event) => this.onChange(event));
      this.inner.addEventListener('keydown', (event) => this.onKeydown(event));
      box.bundle = this;
      this.ready = true;
      this.classList.add('is-ready');
      this.render();
      box.refresh();
      this.checkPurchasable();
    }

    disconnectedCallback() {
      if (this.box?.bundle === this) this.box.bundle = null;
    }

    // Un produit lié repassé en brouillon reste servi par le Liquid : on ne garde que ceux que la boutique vend encore.
    checkPurchasable() {
      Promise.all(
        this.accessories.map((a) =>
          fetch(`${productPath(a.product.handle)}.js`)
            .then((response) => response.ok)
            .catch(() => true)
        )
      ).then((results) => {
        const kept = this.accessories.filter((_, index) => results[index]);
        if (kept.length === this.accessories.length) return;
        this.accessories = kept;
        if (kept.length) this.render();
        else this.remove();
        this.box.refresh();
      });
    }

    selections() {
      return this.accessories.filter((a) => a.checked).map((a) => ({ id: a.variantId, quantity: a.quantity }));
    }

    onVariantChange() {
      this.renderMain();
    }

    accessoryTotals(a) {
      return C.totals([{ quantity: a.quantity, unitCents: a.product.byId.get(a.variantId).price }], null);
    }

    mainQty() {
      return this.box.paliers ? this.box.paliers.selectedQty() : this.box.nativeQty();
    }

    mainTotals() {
      if (this.box.paliers) return this.box.paliers.selectedTotals();
      const variant = this.product.byId.get(this.box.variantId());
      return C.totals([{ quantity: this.mainQty(), unitCents: variant ? variant.price : 0 }], null);
    }

    render() {
      const fmt = this.fmt;
      this.inner.innerHTML = `
        <p class="t3d-bdl__badge">${USERS}<span>${esc(this.badgeText)}</span></p>
        <div class="t3d-card t3d-card--main" data-main></div>
        ${this.accessories
          .map((a, index) => {
            const p = a.product;
            const href = esc(`${productPath(p.handle)}?variant=${a.variantId}`);
            const id = `t3d-acc-${this.dataset.section}-${index}`;
            const words = esc(p.title).split(' ');
            const last = words.pop();
            // Dernier mot + flèche insécables : la flèche ne passe jamais seule à la ligne.
            const name = `${words.length ? `${words.join(' ')} ` : ''}<span class="t3d-nowrap">${last}${NEW_TAB}</span>`;
            // Seul le carré de coche porte le rôle case à cocher (nommé par le produit et son prix) : nom et vignette sont des liens
            // vers la fiche, en nouvel onglet pour garder la sélection en cours ; menu et compteur restent à part (aucun contrôle imbriqué).
            return `<div class="t3d-card${a.checked ? ' is-on' : ''}">
              <div class="t3d-card__head">
                <span class="t3d-check t3d-check--square" role="checkbox" aria-checked="${a.checked}" aria-labelledby="${id}-name ${id}-price" tabindex="0" data-acc="${index}">${CHECK}</span>
                ${p.image ? `<a class="t3d-thumb-link" href="${href}" target="_blank" rel="noopener" tabindex="-1" aria-hidden="true"><img class="t3d-thumb" src="${esc(p.image)}" alt="" width="40" height="40" loading="lazy"></a>` : ''}
                <div class="t3d-card__body"><p class="t3d-card__name"><a class="t3d-card__link" href="${href}" target="_blank" rel="noopener"><span id="${id}-name">${name}</span><span class="visually-hidden"> (nouvel onglet)</span></a></p></div>
                ${priceHtml(this.accessoryTotals(a), fmt, true, `${id}-price`)}
              </div>
              <div class="t3d-card__panel${p.image ? ' t3d-card__panel--thumb' : ''} t3d-controls">
                ${p.single ? '' : variantSelect(p, a.variantId, `data-acc-variant="${index}"`, `Variante · ${p.title}`)}
                ${stepper(a.quantity, `data-acc-qty="${index}"`, p.title)}
              </div>
            </div>`;
          })
          .join('')}
        <p class="t3d-bdl__total" data-total hidden></p>`;
      this.renderMain();
    }

    renderMain() {
      if (!this.inner) return;
      const fmt = this.fmt;
      const qty = this.mainQty();
      const variant = this.product.byId.get(this.box.variantId());
      const main = this.mainTotals();
      // Avec des paliers, les unités peuvent mêler plusieurs variantes ; avec la quantité native, elles sont toutes identiques.
      const showVariant = variant && !this.product.single && (qty === 1 || !this.box.paliers);
      this.inner.querySelector('[data-main]').innerHTML = `<div class="t3d-card__head"><span class="t3d-tag">Inclus</span><div class="t3d-card__body"><p class="t3d-card__name">Ce produit${
        qty > 1 ? ` · ${qty}${NBSP}articles` : ''
      }</p>${showVariant ? `<span class="t3d-card__variant">${esc(variant.title)}</span>` : ''}</div>${priceHtml(main, fmt, true)}</div>`;
      const chosen = this.accessories.filter((a) => a.checked);
      const totalEl = this.inner.querySelector('[data-total]');
      totalEl.hidden = !chosen.length;
      if (!chosen.length) return;
      const sum = chosen
        .map((a) => this.accessoryTotals(a))
        .reduce(
          (acc, t) => ({ fullCents: acc.fullCents + t.fullCents, discountCents: acc.discountCents + t.discountCents, finalCents: acc.finalCents + t.finalCents }),
          main
        );
      totalEl.innerHTML = `<span>Total</span>${priceHtml(sum, fmt)}`;
    }

    update(selector) {
      this.render();
      this.box.refresh();
      refocus(this.inner, selector);
    }

    onClick(event) {
      const step = event.target.closest('[data-step]');
      if (step) return this.onStep(step);
      if (event.target.closest('a, select, .t3d-card__panel')) return; // lien vers la fiche : pas de bascule
      const box = event.target.closest('.t3d-card')?.querySelector('[data-acc]');
      if (box) this.toggle(Number(box.dataset.acc));
    }

    toggle(index) {
      const a = this.accessories[index];
      a.checked = !a.checked;
      this.update(`[data-acc="${index}"]`);
    }

    onStep(button) {
      const index = Number(button.closest('[data-acc-qty]').dataset.accQty);
      const a = this.accessories[index];
      const next = a.quantity + (button.dataset.step === '1' ? 1 : -1);
      a.quantity = Math.min(Math.max(next, 1), MAX_ACCESSORY_QTY);
      this.update(`[data-acc-qty="${index}"] [data-step="${button.dataset.step}"]`);
    }

    onChange(event) {
      const select = event.target.closest('select[data-acc-variant]');
      if (!select) return;
      const index = Number(select.dataset.accVariant);
      this.accessories[index].variantId = Number(select.value);
      this.update(`select[data-acc-variant="${index}"]`);
    }

    onKeydown(event) {
      const card = event.target.closest('[data-acc]');
      if (!card || event.target !== card || (event.key !== ' ' && event.key !== 'Enter')) return;
      event.preventDefault();
      this.toggle(Number(card.dataset.acc));
    }
  }

  customElements.define('t3d-bundle', T3DBundle);
})();
